import React, { useState, useCallback, useEffect } from 'react';
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocaleChange } from '../../../hooks/useLocaleChange';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { getErrorMessage } from '../../../api/utils/error-handler';
import { wikiNewApi } from '../../../api/wiki-new.service';
import { WikiSearchResponseDto, WikiSearchSortBy } from '../../../types';
import WikiSearchBar, { SEARCH_DEFAULT_SORT, normalizeSearchSort } from './WikiSearchBar';
import WikiSearchResults, { WikiSearchSelected } from './WikiSearchResults';
import WikiDetailPanel from './WikiDetailPanel';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { BORDER_COLORS } from '../../../utils/colors';
import { SCROLL_RESERVE } from '../../../utils/responsive';

/** Область поиска: undefined = по всей вики; иначе одна из трёх главных групп. */
export type WikiSearchScope = 'items' | 'skills' | 'npcs';

const MIN_LEN = 2;

interface WikiSearchProps {
  /** Ограничить поиск одной группой (для страниц Предметы/Классы/НПЦ). Undefined — глобальный поиск. */
  source?: WikiSearchScope;
  /** Контент над строкой поиска (обычно WikiBreadcrumbs). */
  breadcrumbs?: React.ReactNode;
  /** Контент, когда поиск не активен (сетка карточек категорий/подкатегорий). */
  children: React.ReactNode;
}

/**
 * Переиспользуемый блок поиска по вики: строка + результаты + боковая панель + запоминание в URL (?q=).
 * Клик по результату открывает карточку справа (WikiDetailPanel), на мобилке (<870px) — переход на страницу.
 * Используется на /wiki (глобально) и на страницах трёх главных категорий (scoped через `source`).
 */
const WikiSearch: React.FC<WikiSearchProps> = ({ source, breadcrumbs, children }) => {
  const { t } = useTranslation('wiki');
  const { showError } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialSort = normalizeSearchSort(searchParams.get('sortBy'));
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isSidebarVisible = useMediaQuery('(min-width: 870px)');

  const [query, setQuery] = useState<string | null>(null);
  const [results, setResults] = useState<WikiSearchResponseDto | null>(null);
  const [previewItem, setPreviewItem] = useState<WikiSearchSelected | null>(null);
  const [sortBy, setSortBy] = useState(initialSort);
  // true уже на ПЕРВОМ рендере, если в URL есть ?q= — чтобы не мелькнула сетка карточек, пока грузятся результаты
  const [searching, setSearching] = useState(initialQuery.trim().length >= MIN_LEN);

  const runSearch = useCallback(
    async (q: string, sort: string = sortBy) => {
      setQuery(q);
      setSearching(true);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('q', q);
          // В URL — только когда не дефолт, как у фильтров страниц-списков
          if (sort === SEARCH_DEFAULT_SORT) p.delete('sortBy');
          else p.set('sortBy', sort);
          return p;
        },
        { replace: true }
      );
      try {
        const data = await wikiNewApi.globalSearch(q, source, sort as WikiSearchSortBy);
        setResults(data);
      } catch (err: unknown) {
        setResults(null);
        showError(getErrorMessage(err, t('search.error')));
      } finally {
        setSearching(false);
      }
    },
    [showError, t, setSearchParams, source, sortBy]
  );

  const handleClear = useCallback(() => {
    setQuery(null);
    setResults(null);
    setPreviewItem(null);
    setSearching(false);
    setSortBy(SEARCH_DEFAULT_SORT);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('q');
        p.delete('sortBy');
        return p;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Сортировка применяется сразу (как в фильтрах страниц-списков): активный поиск
  // перезапрашивается, без запроса — просто запоминаем выбор в URL.
  const handleSortChange = useCallback(
    (value: string) => {
      setSortBy(value);
      if (query) {
        runSearch(query, value);
        return;
      }
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (value === SEARCH_DEFAULT_SORT) p.delete('sortBy');
          else p.set('sortBy', value);
          return p;
        },
        { replace: true }
      );
    },
    [query, runSearch, setSearchParams]
  );

  const handlePreview = useCallback((src: string, id: number) => {
    setPreviewItem({ source: src, id });
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewItem(null);
  }, []);

  // Восстановить поиск из URL при первом заходе
  useEffect(() => {
    if (initialQuery.trim().length >= MIN_LEN) runSearch(initialQuery.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Перезагрузить результаты на смену языка (имена локализованы)
  useLocaleChange(
    useCallback(() => {
      if (query) runSearch(query);
    }, [query, runSearch])
  );

  const isPreviewOpen = isSidebarVisible && previewItem !== null;

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: isPreviewOpen ? 2 : 0 }}>
      {/* Left: поиск + результаты/карточки. minHeight:0 обязателен — иначе колонка растёт по контенту,
          SimpleBar не ограничивается по высоте, карточки вылезают за подложку и обрезаются (в т.ч. на hover). */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, color: 'white' }}>
        {breadcrumbs}
        <WikiSearchBar
          onSearch={runSearch}
          onClear={handleClear}
          initialQuery={initialQuery}
          sortBy={sortBy}
          onSortChange={handleSortChange}
        />
        {/* Резерв справа: с sm — под полосу скролла (12px); на мобиле полоса скрыта глобально
            (`.simplebar-track { display: none }` в index.css), но пара пикселей нужна — SimpleBar
            режет содержимое ровно по своей границе, и при дробной ширине колонок (393px → 143.5px)
            правый край карточки попадает на половину пикселя и при dpr=1 срезается с закруглением */}
        <SimpleBar style={{ flex: 1, minHeight: 0, paddingRight: isSm ? SCROLL_RESERVE.sm : SCROLL_RESERVE.xs }} autoHide={false}>
          {/* Немного отступа ВНУТРИ SimpleBar — чтобы у поднятия карточки на hover (translateY -6px) было место
              и её верх не обрезался краем overflow. Основной зазор даёт mb бара (снаружи), чтобы полоса скролла
              начиналась ниже бара, как на страницах-списках. */}
          <Box sx={{ pt: { xs: 1, sm: 1.25 } }}>
            {results !== null ? (
              <WikiSearchResults
                results={results}
                onPreview={isSidebarVisible ? handlePreview : undefined}
                selected={previewItem}
                isPreviewOpen={isPreviewOpen}
              />
            ) : searching ? (
              // Идёт поиск (в т.ч. восстановление из ?q=) — не показываем карточки, чтобы не мелькали
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              children
            )}
          </Box>
        </SimpleBar>
      </Box>

      {/* Divider + Detail panel (как в WikiCategoryPage) */}
      {isPreviewOpen && previewItem && (
        <>
          <Box sx={{ width: '1px', background: BORDER_COLORS.DEFAULT, flexShrink: 0 }} />
          <Box
            sx={{
              flex: { xs: '1', sm: '0 0 40%', md: '0 0 30%', lg: '0 0 35%', xl: '0 0 35%' },
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            <WikiDetailPanel
              source={previewItem.source}
              itemId={previewItem.id}
              detailUrl={`/wiki/${previewItem.source}/details/${previewItem.id}`}
              onClose={handleClosePreview}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default WikiSearch;
